import type { ReactNode } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { effectiveRubricStatus, type AiDynamicSectionRubricPoint, type RubricStatus } from './aiDynamicSection';

export interface AiDynamicSectionRubricChecklistProps {
  rubric: AiDynamicSectionRubricPoint[];
  /** Raw field off the row — pass it through untouched; this component applies the legacy
   *  fallback itself via `effectiveRubricStatus`. */
  rubricStatus?: RubricStatus;
  /** True once client-side polling for a pending rubric gave up waiting. Displayed identically to
   *  a backend-reported failure — the author does not need to know the difference, and this
   *  component never claims the backend actually failed when it might still finish later. */
  pollTimedOut?: boolean;
  /** Rendered only under the failed message (backend failure or a timed-out poll) — e.g. a
   *  regenerate button. Omit in a read-only context: nothing here should let the reader act. */
  actions?: ReactNode;
}

const RUBRIC_GENERATED_ON_SAVE_TEXT =
  'The rubric will be generated from the Goal and Topics above when the survey is saved.';

const RUBRIC_REVIEW_HELPER_TEXT =
  'What a great answer should cover, generated by AI from the Goal, Topics, probing instructions, '
  + 'and linked questions above. Open this question for editing to correct a point directly, or '
  + 'update those inputs and regenerate.';

const RUBRIC_PENDING_TEXT =
  "The AI is writing this section's checklist now. The question is already saved — this will "
  + 'fill in on its own once the checklist is ready.';

const RUBRIC_FAILED_TEXT =
  "The checklist couldn't be written for this section. The question itself is still saved and "
  + 'usable — try again to generate the checklist.';

const RUBRIC_TIER_LABELS: Record<number, string> = {
  0: 'Tier 0 (core)',
  1: 'Tier 1 (functional)',
  2: 'Tier 2 (value)',
};

const rubricTierLabel = (tier: number): string => RUBRIC_TIER_LABELS[tier] ?? `Tier ${tier}`;

/**
 * Renders the rubric/checklist area for one `ai_dynamic_section` question, covering every state
 * `rubric_status` can resolve to. Read-only: no input, textarea, or button ever appears for a
 * point here (the failed-state `actions` slot is the one exception, and only for a caller-supplied
 * retry control). Used by the always-on `AiDynamicSectionSummary` for every state, and by the
 * editable `AiDynamicSectionSettings` panel for every state EXCEPT "ready with points" — once a
 * checklist exists there, the panel swaps to `AiDynamicSectionRubricEditor` instead, since the
 * product owner ruled the author may correct an already-generated point directly. Never grow this
 * component to allow that itself; the "no edit anywhere" invariant is what makes the read-only
 * summary trustworthy as read-only.
 */
export function AiDynamicSectionRubricChecklist({
  rubric,
  rubricStatus,
  pollTimedOut,
  actions,
}: AiDynamicSectionRubricChecklistProps) {
  const status = pollTimedOut ? 'failed' : effectiveRubricStatus(rubricStatus);

  if (status === 'pending') {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-blue-600" />
        <p className="text-sm text-blue-900">{RUBRIC_PENDING_TEXT}</p>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
          <p className="text-sm text-red-900">{RUBRIC_FAILED_TEXT}</p>
        </div>
        {actions && <div className="mt-2 flex flex-wrap gap-2">{actions}</div>}
      </div>
    );
  }

  if (rubric.length === 0) {
    return <p className="text-xs text-gray-500">{RUBRIC_GENERATED_ON_SAVE_TEXT}</p>;
  }

  return (
    <>
      <p className="text-xs text-gray-500 mb-2">{RUBRIC_REVIEW_HELPER_TEXT}</p>
      <div className="space-y-2">
        {rubric.map((point) => (
          <div key={point.id} className="border border-gray-100 rounded-lg p-2 space-y-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-gray-800 flex-1">{point.text}</p>
              <span className="text-xs text-gray-500 whitespace-nowrap">{rubricTierLabel(point.tier)}</span>
            </div>
            {point.guidance && <p className="text-xs text-gray-500">{point.guidance}</p>}
          </div>
        ))}
      </div>
    </>
  );
}
